import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../../Services/api";

import "./cadastroQuestaoOpcoesModal.css";

const ordenarPorOrdem = (lista = []) =>
    [...lista].sort((a, b) => {
        const ordemA = a.ordem ?? 0;
        const ordemB = b.ordem ?? 0;
        if (ordemA !== ordemB) return ordemA - ordemB;
        return (a.IDQuestao_Opcao ?? 0) - (b.IDQuestao_Opcao ?? 0);
    });

const calcularProximaOrdem = (lista = []) => {
    const usadas = new Set();
    let max = 0;
    lista.forEach((item) => {
        const ordem = Number(item?.ordem);
        if (Number.isFinite(ordem) && ordem > 0) {
            usadas.add(ordem);
            if (ordem > max) max = ordem;
        }
    });
    for (let i = 1; i <= max + 1; i += 1) {
        if (!usadas.has(i)) return i;
    }
    return max + 1;
};

const CadastroQuestaoOpcoesModal = ({ open, IDQuestao, tituloQuestao, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [opcoes, setOpcoes] = useState([]);
    const [novaOpcao, setNovaOpcao] = useState({ descricao: "", ordem: 1 });

    const carregarOpcoes = async () => {
        if (!IDQuestao) return;
        try {
            setLoading(true);
            const response = await api.get(`/chkquestaoopcoes/${IDQuestao}`, {
                params: { IDQuestao },
            });
            const lista = ordenarPorOrdem(response.data ?? []);
            setOpcoes(lista);
            setNovaOpcao((prev) => ({
                ...prev,
                ordem: calcularProximaOrdem(lista),
            }));
        } catch (error) {
            console.error("Erro ao carregar opções:", error);
            Swal.fire("Erro", "Não foi possível carregar as opções.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        if (!IDQuestao) return;
        carregarOpcoes();
    }, [open, IDQuestao]);

    const atualizarOpcao = (id, campo, valor) => {
        setOpcoes((prev) =>
            prev.map((opcao) =>
                opcao.IDQuestao_Opcao === id ? { ...opcao, [campo]: valor } : opcao
            )
        );
    };

    const salvarOpcao = async (opcao) => {
        if (!opcao?.descricao?.trim()) {
            return Swal.fire("Atenção", "Informe a descrição da opção.", "warning");
        }
        const ordem = Number(opcao.ordem);
        if (!Number.isFinite(ordem) || ordem <= 0) {
            return Swal.fire("Atenção", "Informe uma ordem válida.", "warning");
        }

        try {
            setSavingId(opcao.IDQuestao_Opcao);
            await api.put(`/chkquestaoopcoes/${opcao.IDQuestao_Opcao}`, {
                IDQuestao,
                descricao: opcao.descricao.trim(),
                ordem,
            });
            await carregarOpcoes();
        } catch (error) {
            console.error("Erro ao salvar opção:", error);
            Swal.fire("Erro", "Não foi possível salvar a opção.", "error");
        } finally {
            setSavingId(null);
        }
    };

    const excluirOpcao = async (opcao) => {
        const confirm = await Swal.fire({
            title: "Excluir opção?",
            text: "Essa ação é irreversível.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
            cancelButtonColor: "var(--color-btnCancelar-danger)",
            customClass: {
                cancelButton: "swal-cancel-danger",
            },
        });

        if (!confirm.isConfirmed) return;

        try {
            setSavingId(opcao.IDQuestao_Opcao);
            await api.delete(`/chkquestaoopcoes/${opcao.IDQuestao_Opcao}`);
            await carregarOpcoes();
        } catch (error) {
            console.error("Erro ao excluir opção:", error);
            Swal.fire("Erro", "Não foi possível excluir a opção.", "error");
        } finally {
            setSavingId(null);
        }
    };

    const adicionarOpcao = async () => {
        if (!novaOpcao.descricao.trim()) {
            return Swal.fire("Atenção", "Informe a descrição da opção.", "warning");
        }
        const ordem = Number(novaOpcao.ordem);
        if (!Number.isFinite(ordem) || ordem <= 0) {
            return Swal.fire("Atenção", "Informe uma ordem válida.", "warning");
        }

        try {
            setSavingId("nova");
            await api.post(`/chkquestaoopcoes`, {
                IDQuestao,
                descricao: novaOpcao.descricao.trim(),
                ordem,
            });
            setNovaOpcao({ descricao: "", ordem: ordem + 1 });
            await carregarOpcoes();
        } catch (error) {
            console.error("Erro ao adicionar opção:", error);
            Swal.fire("Erro", "Não foi possível adicionar a opção.", "error");
        } finally {
            setSavingId(null);
        }
    };

    if (!open) return null;

    return (
        <div className="cadQuestao-modal-overlay">
            <div className="cadQuestao-modal">
                <h3 className="cadQuestao-title">Opções da Questão</h3>

                <div className="cadQuestao-form">
                    <div className="cadQuestao-field">
                        <label>Questão</label>
                        <input type="text" value={tituloQuestao || ""} disabled />
                    </div>

                    {loading && <p>Carregando...</p>}

                    {!loading && (
                        <>
                            <div className="cadQuestaoOpcoes-list">
                                {opcoes.length === 0 && (
                                    <p className="cadQuestaoOpcoes-empty">
                                        Nenhuma opção cadastrada.
                                    </p>
                                )}

                                {opcoes.map((opcao) => (
                                    <div key={opcao.IDQuestao_Opcao} className="cadQuestaoOpcoes-row">
                                        <input
                                            type="text"
                                            value={opcao.descricao}
                                            onChange={(e) =>
                                                atualizarOpcao(
                                                    opcao.IDQuestao_Opcao,
                                                    "descricao",
                                                    e.target.value
                                                )
                                            }
                                            maxLength={150}
                                        />
                                        <input
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={opcao.ordem ?? ""}
                                            onChange={(e) =>
                                                atualizarOpcao(
                                                    opcao.IDQuestao_Opcao,
                                                    "ordem",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="cadQuestaoOpcoes-actions">
                                            <button
                                                type="button"
                                                onClick={() => salvarOpcao(opcao)}
                                                disabled={savingId === opcao.IDQuestao_Opcao}
                                            >
                                                {savingId === opcao.IDQuestao_Opcao
                                                    ? "Salvando..."
                                                    : "Salvar"}
                                            </button>
                                            <button
                                                type="button"
                                                className="danger"
                                                onClick={() => excluirOpcao(opcao)}
                                                disabled={savingId === opcao.IDQuestao_Opcao}
                                            >
                                                Excluir
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="cadQuestaoOpcoes-new">
                                <h4>Nova opção</h4>
                                <div className="cadQuestaoOpcoes-row">
                                    <input
                                        type="text"
                                        placeholder="Descrição"
                                        value={novaOpcao.descricao}
                                        onChange={(e) =>
                                            setNovaOpcao((prev) => ({
                                                ...prev,
                                                descricao: e.target.value,
                                            }))
                                        }
                                        maxLength={150}
                                    />
                                    <input
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={novaOpcao.ordem ?? ""}
                                        onChange={(e) =>
                                            setNovaOpcao((prev) => ({
                                                ...prev,
                                                ordem: e.target.value,
                                            }))
                                        }
                                    />
                                    <div className="cadQuestaoOpcoes-actions">
                                        <button
                                            type="button"
                                            onClick={adicionarOpcao}
                                            disabled={savingId === "nova"}
                                        >
                                            {savingId === "nova" ? "Salvando..." : "Adicionar"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="cadQuestao-buttons">
                        <button className="cadQuestao-btn-cancel" onClick={onClose}>
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CadastroQuestaoOpcoesModal;
